import React, { BaseSyntheticEvent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { InputLabel, Grid, Radio } from '@material-ui/core';
import { HowToReg } from '@material-ui/icons';
import {
  CustomizedDialog,
  CustomizedTypography,
  CustomizedButton
} from '@components/Customized';
import { InputItem } from '@components/Input/Inputs';
import useClient from '@hooks/useClient';
import useUserActionLog, {
  Enum_Action_Button_Enum,
  CapaActivityType
} from '@hooks/useUserActionLog';
import {
  CapaQuotationUserBizType,
  useVerifyCompanyRegistrationNumberQuery,
  CapaErrorCode,
  UpdateQuotationUserInput
} from '@generated/graphql';
import useStyles from './styles';

interface TypeSelectForm {
  bizType?: CapaQuotationUserBizType | string;
  organizationName?: string | null;
  companyRegistrationNumber?: string | null;
  id?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onUpdate: (data: UpdateQuotationUserInput) => void;
}

const ClientTypeEditDialog: React.FC<Props> = ({
  open,
  onClose,
  onUpdate
}: Props) => {
  const {
    clientState: { id, organizationName, companyRegistrationNumber, bizType }
  } = useClient();
  const { onInsertUserActionLogOne } = useUserActionLog();

  const [isIndividual, setIsIndividual] = useState<boolean>(false);

  const clientTypes: Array<{
    buttonType: CapaQuotationUserBizType;
    text: string;
  }> = [
    { buttonType: CapaQuotationUserBizType.Biz, text: '사업자' },
    {
      buttonType: CapaQuotationUserBizType.Individual,
      text: '비사업자'
    }
  ];

  const {
    register,
    handleSubmit,
    errors,
    formState,
    setValue,
    getValues,
    trigger,
    reset
  } = useForm<TypeSelectForm>({
    mode: 'onChange'
  });

  const { isValid } = formState;
  const [
    selectedType,
    setSelectedType
  ] = useState<CapaQuotationUserBizType | null>(null);
  const { refetch } = useVerifyCompanyRegistrationNumberQuery({
    skip: true
  });
  const classes = useStyles();

  const initialTypeSelectForm: TypeSelectForm = {
    bizType: '',
    organizationName: ''
  };

  const beforeSubmit = (data: TypeSelectForm) => {
    const updateForm = {
      ...initialTypeSelectForm,
      ...data,
      id,
      bizType: selectedType
    };
    if (updateForm.bizType === CapaQuotationUserBizType.Individual) {
      updateForm.organizationName = '';
    }
    return updateForm;
  };

  const onSubmit = (
    data: TypeSelectForm,
    e: BaseSyntheticEvent<object, any, any> | undefined
  ) => {
    e?.preventDefault();
    const updateForm = beforeSubmit(data);
    onInsertUserActionLogOne(
      Enum_Action_Button_Enum.Button_Mypage_Update_IsBiz_Submit,
      CapaActivityType.ButtonClick
    );
    onUpdate(updateForm);
    onClose();
  };

  useEffect(() => {
    setSelectedType(bizType ? (bizType as CapaQuotationUserBizType) : null);
    trigger();
  }, [bizType, trigger]);

  useEffect(() => {
    if (getValues('companyRegistrationNumber')) {
      trigger('companyRegistrationNumber');
    }
    setIsIndividual(false);
  }, [selectedType, trigger, getValues]);

  useEffect(() => {
    if (isIndividual) {
      setValue('organizationName', '');
    }
    trigger('organizationName');
  }, [isIndividual, getValues, setValue, trigger, register]);

  useEffect(() => {
    if (!bizType) return;

    reset({
      bizType: bizType,
      organizationName: organizationName,
      companyRegistrationNumber: companyRegistrationNumber
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <CustomizedDialog
      open={open}
      onClose={onClose}
      title="사업자여부 변경"
      icon={<HowToReg />}
      color="primary"
      maxWidth={476}
      closeButton
    >
      <Grid container>
        <CustomizedTypography
          gutterBottoms={16}
          variant="body2"
          fontWeight="normal"
          color="textSecondary"
        >
          사업자 여부
        </CustomizedTypography>
        {clientTypes.map((data) => {
          return (
            <CustomizedButton
              className={classes.actionButton}
              classes={{ label: classes.bizBtnLabel }}
              onClick={() => setSelectedType(data.buttonType)}
              variant={'text'}
              color="default"
              key={data.buttonType}
            >
              <Radio
                color="primary"
                checked={data.buttonType === selectedType}
                classes={{ root: classes.radio }}
              />
              {data.text}
            </CustomizedButton>
          );
        })}
      </Grid>

      {selectedType && (
        <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
          {selectedType === CapaQuotationUserBizType.Biz && (
            <div>
              <Grid container wrap="nowrap" justifyContent="space-between">
                <InputLabel className={classes.inputLabel} required>
                  {selectedType === 'BIZ' ? '회사명' : '단체명'}
                </InputLabel>
              </Grid>
              <InputItem
                name="organizationName"
                errors={errors}
                inputProps={{
                  placeholder: '회사명 * ',
                  disabled: isIndividual,
                  ref: register({
                    required: !isIndividual
                  })
                }}
                marginBottom={12}
              />
            </div>
          )}

          {selectedType === CapaQuotationUserBizType.Biz && (
            <>
              <InputLabel className={classes.inputLabel} required>
                사업자등록번호
              </InputLabel>
              <InputItem
                name="companyRegistrationNumber"
                errors={errors}
                inputProps={{
                  placeholder: '사업자등록번호 * (숫자만 입력)',
                  type: 'text',
                  maxLength: 10,
                  onChange: (e: any) =>
                    (e.currentTarget.value = e.currentTarget.value.trim()),
                  ref: register({
                    required: true,
                    pattern: /^[0-9]*$/,
                    minLength: 10,
                    maxLength: 10,
                    validate: {
                      checkNumber: async (value) => {
                        if (!value) {
                          return true;
                        }
                        const { data, loading, error } = await refetch({
                          input: {
                            companyRegistrationNumber: value
                          }
                        });
                        const result =
                          data?.verifyCompanyRegistrationNumber?.result;
                        if (loading) return '조회중입니다';
                        if (error) return '사업자등록번호 조회에 실패했습니다.';
                        switch (result) {
                          case CapaErrorCode.Success:
                            return true;
                          case CapaErrorCode.DuplicateCompanyRegistrationNo:
                            return true;
                          case CapaErrorCode.InavlidCompanyRegistrationNo:
                            return '등록되지 않은 사업자등록번호 입니다.';
                          case CapaErrorCode.ParmeterValidationError:
                            return '잘못된 형식입니다.';
                          default:
                            return '사업자등록번호 조회에 실패했습니다.';
                        }
                      }
                    }
                  })
                }}
                marginBottom={12}
              />
            </>
          )}
          <CustomizedButton
            className={classes.submitButton}
            type="submit"
            variant="contained"
            color="primary"
            disabled={!isValid}
          >
            변경하기
          </CustomizedButton>
        </form>
      )}
    </CustomizedDialog>
  );
};

export default ClientTypeEditDialog;
